import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  getSidebarCollapsedPreference,
  resolvePreferenceUserId,
  setSidebarCollapsedPreference,
} from '../shared/utils/user-preferences.util';

export interface SidebarCollapsedOptions {
  /** When false, updates in-memory state only (e.g. campaign editor temporary collapse). */
  persist?: boolean;
}

/**
* Centralized navigation state service that manages and exposes the open/closed state of the mobile navigation and the collapsed/expanded state of the sidebar across the application.
* @service
* @providedIn root
* @example
*   // Inject into a component or another service:
*   constructor(private navigationService: NavigationService) {}
* @input none - This service does not accept Angular @Input bindings; it is used programmatically via its API and observables.
* @output Observable<boolean> isMobileNavOpen$ - Observable that emits the current open/closed state of the mobile navigation. Emit values: true = open, false = closed.
* @output Observable<boolean> isSidebarCollapsed$ - Observable that emits whether the sidebar is currently collapsed (true) or expanded (false).
* @method toggleMobileNav() - Toggles the current mobile navigation open state (inverts the current value and emits it).
* @method setMobileNavOpen(isOpen: boolean) - Sets the mobile navigation open state explicitly to the provided boolean value and emits it.
* @method toggleSidebarCollapse() - Toggles the sidebar collapsed state (inverts the current value and emits it).
* @method setSidebarCollapsed(collapsed: boolean) - Sets the sidebar collapsed state explicitly to the provided boolean value and emits it.
* @lifecycle None - This injectable is a singleton (providedIn: 'root') and does not implement Angular lifecycle hooks; it relies on consumers to subscribe/unsubscribe to the exposed observables as needed.
*/
@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private isMobileNavOpen = new BehaviorSubject<boolean>(false);
  isMobileNavOpen$ = this.isMobileNavOpen.asObservable();

  private isSidebarCollapsed = new BehaviorSubject<boolean>(this.readPersistedSidebarCollapsed());
  isSidebarCollapsed$ = this.isSidebarCollapsed.asObservable();

  constructor() {
    this.restoreSidebarCollapsedForCurrentUser();
  }

  toggleMobileNav() {
    this.isMobileNavOpen.next(!this.isMobileNavOpen.value);
  }

  setMobileNavOpen(isOpen: boolean) {
    this.isMobileNavOpen.next(isOpen);
  }

  toggleSidebarCollapse() {
    this.setSidebarCollapsed(!this.isSidebarCollapsed.value);
  }

  setSidebarCollapsed(collapsed: boolean, options?: SidebarCollapsedOptions) {
    this.isSidebarCollapsed.next(collapsed);
    if (options?.persist === false) {
      return;
    }
    const userId = resolvePreferenceUserId();
    if (userId) {
      setSidebarCollapsedPreference(userId, collapsed);
    }
  }

  /** Reload sidebar collapsed state for the logged-in user (after login / user switch). */
  restoreSidebarCollapsedForCurrentUser(): void {
    this.isSidebarCollapsed.next(this.readPersistedSidebarCollapsed());
  }

  getSidebarCollapsed(): boolean {
    return this.isSidebarCollapsed.value;
  }

  private readPersistedSidebarCollapsed(): boolean {
    const userId = resolvePreferenceUserId();
    if (!userId) {
      return false;
    }
    return getSidebarCollapsedPreference(userId) ?? false;
  }
}
